import { Button, Card, CardContent, CardHeader, Grid, Stack, Typography } from '@mui/material'
import { ContainerResponseDto } from '@planning/app/api'
import { FeatureFlag } from '@planning/components/FeatureFlags'
import { useTranslate } from '@tolgee/react'
import { HighlightedText, useContainerAttributesParameters, useMinimalsTheme } from '@tom-ui/ui'
import { formatWeight, PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { useNavigate } from 'react-router'

interface Props extends ContainerResponseDto {
  filter?: string
  onClickEdit?: () => void
}

interface AttributeProps {
  filter?: string
  label: string
  value: string | number
  size: number
  highlightFilteredValue?: boolean
}

const ContainerMasterDataCardAttribute: FC<AttributeProps> = ({
  filter,
  label,
  value,
  size,
  highlightFilteredValue,
}) => {
  const theme = useMinimalsTheme()
  const stringValue = value.toLocaleString()

  return (
    <Grid size={size}>
      <Stack mt={theme.customSpacing.s}>
        <Typography variant='body1'>{label}</Typography>
        <Typography variant='h4'>
          <HighlightedText
            color={theme.palette.info.main}
            highlight={highlightFilteredValue ? filter?.toUpperCase() : ''}
          >
            {stringValue.toUpperCase()}
          </HighlightedText>
        </Typography>
      </Stack>
    </Grid>
  )
}

export const ContainerMasterDataCard = observer((props: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const theme = useMinimalsTheme()
  const { types: containerTypeParameters, heights: containerHeightParameters } =
    useContainerAttributesParameters()

  const {
    id,
    filter,
    number,
    isoCode,
    length,
    height,
    type,
    tare,
    maxGross,
    operator,
    onClickEdit,
  } = props

  const attributes = [
    {
      label: t('isoCode', 'isoCode'),
      value: isoCode && isoCode !== '' ? isoCode?.toUpperCase() : t('unknown', 'Unknown'),
      size: 1,
      highlightFilteredValue: true,
    },
    {
      label: t('length', 'Length'),
      value: length ? `${length}ft` : t('unknown', 'Unknown'),
      size: 1,
    },
    {
      label: t('height', 'Height'),
      value: containerHeightParameters[height]?.label ?? t('unknown', 'Unknown'),
      size: 1,
    },
    {
      label: t('type', 'Type'),
      value: containerTypeParameters[type]?.label ?? t('unknown', 'Unknown'),
      size: 2,
    },
    { label: t('tare', 'Tare'), value: formatWeight(tare), size: 1 },
    { label: t('maxGross', 'Max Gross'), value: formatWeight(maxGross), size: 1 },
    {
      label: t('maxPayload', 'Max Payload'),
      value: maxGross && tare ? formatWeight(maxGross - tare) : t('unknown', 'Unknown'),
      size: 1,
    },
    {
      label: t('operator', 'Operator'),
      value: operator && operator !== '' ? operator : t('unknown', 'Unknown'),
      size: 4,
    },
  ]

  const handleHistoryClick = () => {
    navigate(`${PATHS.masterData.containers.url}/${id}/${PATHS.masterData.containers.history.url}`)
  }

  return (
    <Card
      sx={{
        marginBottom: theme.customSpacing.m,
      }}
    >
      <CardHeader
        action={
          <Stack direction='row' gap={1}>
            <Button color='inherit' onClick={onClickEdit}>
              {t('view', 'View')}
            </Button>
            <FeatureFlag name='container-history'>
              <Button color='inherit' onClick={handleHistoryClick}>
                {t('history', 'History')}
              </Button>
            </FeatureFlag>
          </Stack>
        }
        title={
          <Typography variant='h2'>
            <HighlightedText color={theme.palette.info.main} highlight={filter?.toUpperCase()}>
              {number?.toUpperCase()}
            </HighlightedText>
          </Typography>
        }
      />
      <CardContent sx={{ minHeight: '100%' }}>
        <Stack direction='row' spacing={theme.customSpacing.xll}>
          {attributes.map(attribute => (
            <ContainerMasterDataCardAttribute
              key={attribute.label}
              {...attribute}
              filter={filter}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
})
